import React, { useMemo } from 'react'
import Sheet from 'components/utils/Sheet'
import BedbankMultiRoomPricing from './BedbankMultiRoomPricing'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { useBedbankMultiRoomAddToUserCartCallback, useBedbankMultiRoomBookNowCallback } from 'hooks/OfferPage/useBedbankBuyCallbacks'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import useBedbankSelectedPackageViewsByRoom from 'hooks/OfferPage/useBedbankSelectedPackageViewsByRoom'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import { getEnquirySubscriber } from 'luxPlus/selectors/checkout/order'
import moment from 'moment'
import Group from 'components/utils/Group'
import { FLIGHTS_MIN_LEAD_TIME_DAYS } from 'constants/flight'

interface Props {
  rooms: Array<OfferPageRoom>
  offer: App.BedbankOffer
  checkInDate?: string
  checkOutDate?: string
  subscriber?: App.OrderSubscriber
  isSpoofed?: boolean
  isLoggedIn?: boolean
  originAirport?: App.Airport
  connectRooms: boolean
  withFlights: boolean
}

function BedbankMultiRoomBuySection({
  offer,
  rooms,
  checkInDate,
  checkOutDate,
  subscriber,
  isLoggedIn,
  originAirport,
  isSpoofed,
  connectRooms,
  withFlights,
}: Props) {
  const [roomsWithData, packageViewsLoading] = useBedbankSelectedPackageViewsByRoom(offer, rooms)

  const flightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkInDate,
    endDate: checkOutDate,
    originAirportCode: originAirport?.code,
  })

  const departureTooEarly = useMemo(() => {
    return !!checkInDate && moment(checkInDate).diff(moment(), 'days') < FLIGHTS_MIN_LEAD_TIME_DAYS
  }, [checkInDate])

  const onBookNow = useBedbankMultiRoomBookNowCallback({
    offer,
    roomsWithData,
    checkInDate,
    checkOutDate,
    originAirport,
    isLoggedIn,
    isSpoofed,
    subscriber,
    flightPrice,
    connectRooms,
    withFlights,
  })

  const onAddToUserCartNow = useBedbankMultiRoomAddToUserCartCallback({
    offer,
    roomsWithData,
    checkInDate,
    checkOutDate,
    originAirport,
    isLoggedIn,
    isSpoofed,
    subscriber,
    flightPrice,
    connectRooms,
    withFlights,
  })

  if (packageViewsLoading) {
    return <Sheet padding={20} background="neutral-eight">
      <LoadingIndicator inline />
    </Sheet>
  }

  if (
    !roomsWithData || roomsWithData.length === 0 ||
    // The package autoselection should prevent this from happening, but just in case...
    roomsWithData.some(rwd => !rwd.capacityValid)
  ) {
    return null
  }

  const allRoomsSelected = roomsWithData.length === rooms.length

  return (
    <Sheet padding={20} tabletPadding={0} background="neutral-eight">
      <Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="end" gap={12}>
        <BedbankMultiRoomPricing
          rooms={rooms}
          roomsWithData={roomsWithData}
          onBookNow={onBookNow}
          onAddToUserCartNow={onAddToUserCartNow}
          flightPrice={flightPrice}
          departureTooEarly={departureTooEarly}
          canProceed={allRoomsSelected}
        />
      </Group>
    </Sheet>
  )
}

type MappedProps = Pick<Props,
  | 'rooms'
  | 'offer'
  | 'checkInDate'
  | 'checkOutDate'
  | 'subscriber'
  | 'isLoggedIn'
  | 'isSpoofed'
  | 'originAirport'
  | 'connectRooms'
  | 'withFlights'
>

export default withOfferPageAndReduxState((offerState, appState): MappedProps => ({
  rooms: offerState.rooms,
  offer: offerState.bedbankOffer!,
  checkInDate: offerState.checkInDate,
  checkOutDate: getCheckoutDate(offerState),
  subscriber: getEnquirySubscriber(appState),
  isLoggedIn: !!appState.auth.account.memberId,
  isSpoofed: appState.auth.account.isSpoofed,
  originAirport: offerState.airport,
  connectRooms: !!offerState.isMultiRoomConnected,
  withFlights: offerState.flights,
}), BedbankMultiRoomBuySection)
